<template>
    <LoadingState v-if="graphLoading" overlay></LoadingState>
    <!--TODO, I will leave "lazy" prop here. This has been placed and removed a lot of times -->
    <!-- from now on, please write down the reason why you are removing it or resetting it -->
    <!-- now I've set lazy so that the business rules dialog is not visible in every tab -->
    <Tabs class="graphTabs" value="0" lazy>
        <TabList>
            <Tab value="0" data-cy="graph-tab">Graph</Tab>
            <Tab value="1" v-if="graphId && canEditGraph" data-cy="run-tab">Run</Tab>
            <Tab value="2" v-if="graphId && canEditGraph" data-cy="notes-tab">
                <div class="flex items-center gap-2" data-cy="notes-tab">
                    <span class="whitespace-nowrap">Notes</span>
                    <Tag class="betaTag" rounded severity="warning" value="BETA"></Tag>
                </div>
            </Tab>
            <Tab value="3" v-if="contentPushEnabled && graph && graph.datamodel_id">
                <div class="flex items-center gap-2" data-cy="ems-tab">
                    <span class="font-bold whitespace-nowrap">EMS Assets</span>
                    <Tag class="betaTag" rounded severity="warning" value="BETA"></Tag>
                </div>
            </Tab>
        </TabList>
        <TabPanels>
            <TabPanel value="0" class="relative">
                <!-- The key will allow to remount the component when the value change -->
                <GraphEditor
                    v-if="graphDefinition"
                    :key="graphId + selectedVersion"
                    :graphId="graphId"
                ></GraphEditor>
            </TabPanel>
            <TabPanel value="1" v-if="graphId && canEditGraph">
                <GraphRun
                    v-if="graphDefinition"
                    :graphId="graphId"
                    :key="'run' + graphId"
                />
            </TabPanel>
            <TabPanel value="2" v-if="graphId && canEditGraph">
                <GraphNotes :key="'notes_' + graphId"/>
            </TabPanel>
            <TabPanel value="3" v-if="graph && graph.datamodel_id">
                <EmsAssets></EmsAssets>
            </TabPanel>
        </TabPanels>
    </Tabs>

    <div class="editModeButton flex items-center gap-2">
        <Message v-if="lockError" severity="error">{{ lockError }}</Message>
        <LoadingState v-if="lockLoading" small></LoadingState>
        <Button
            v-if="canEditGraph && !isLocked"
            :label="getEditMode ? 'Exit edit mode' : 'Edit mode'"
            :severity="getEditMode ? 'secondary' : 'primary'"
            :disabled="lockLoading"
            data-cy="edit-mode-button"
            :icon="getEditMode ? 'pi pi-times' : 'pi pi-pencil'"
            @click="toggleEditMode"
            rounded
        />
        <Button
            v-if="isLocked"
            icon="pi pi-lock"
            :disabled="lockLoading"
            @click="checkLockedBy"
            data-cy="locked-button"
            text
            rounded
            v-tooltip.bottom="
        'Another user is editing this graph. Click here to check again'
      "
        />
    </div>
    <Dialog
        v-model:visible="getShowAskToStay"
        modal
        :closable="false"
        style="width: 400px"
        header="Do you want to stay in edit mode?"
    >
        <div class="flex items-center mb-2">
            <i class="pi pi-exclamation-triangle mr-2" style="font-size: 2rem"></i>
            <span
            >Edit mode will automatically turn off in {{ timeLeft }} seconds</span
            >
        </div>
        <div class="flex justify-end gap-2">
            <Button rounded icon="pi pi-times" label="Exit" class="p-button-text" @click="toggleEditMode"/>
            <Button rounded icon="pi pi-check" label="Stay" class="p-button-raised" @click="lockGraph"/>
        </div>
    </Dialog>
</template>
<script setup>
import {computed, defineAsyncComponent, watch, ref, onMounted} from "vue";
import {useStore} from "vuex";

const store = useStore();
import {useRouter} from "vue-router";

const GraphEditor = defineAsyncComponent(() =>
    import("./editor/GraphEditor.vue")
);
const GraphRun = defineAsyncComponent(() => import("./run/GraphRun"));
import {GRAPH_API_TYPE} from "@/src/model/apiTypes";

const EmsAssets = defineAsyncComponent(() => import("./EmsAssets"));
const GraphNotes = defineAsyncComponent(() => import("./GraphNotes"));
const router = useRouter();

const contentPushEnabled = import.meta.env.VITE_APP_CONTENT_PUSH_ENABLED === "true";

const graphId = computed(() => router.currentRoute.value.params.graphId);
const graph = computed(() => store.getters["repoStore/getGraph"]);
const graphDefinition = computed(
    () => store.getters["repoStore/getGraphDefinition"]
);
const canEditGraph = computed(() => store.getters["repoStore/canEditGraph"]);
const selectedVersion = computed(
    () => store.getters["repoStore/getSelectedVersion"]
);

const graphLoading = computed(() =>
    store.getters.isLoading(GRAPH_API_TYPE.GET_GRAPH)
);

const lockError = ref(null);

const error = computed(() => store.getters.getError);
const timeLeft = ref(60);

const setLockError = (e) => {
    lockError.value = e.response.data.error.message;
    setTimeout(() => {
        lockError.value = null;
    }, 5000);
};

const checkLockedBy = () => store.dispatch("graphStore/checkLockedBy");

watch(error, (val) => {
    if (
        val &&
        val.code == "ERR_BAD_REQUEST" &&
        val.response.data.error.name == "GRAPH_LOCKED"
    ) {
        // the graph is locked by another user
        checkLockedBy();
        setLockError(val);
    }
    if (
        val &&
        val.code == "ERR_BAD_REQUEST" &&
        val.response.data.error.name == "GRAPH_NOT_LOCKED"
    ) {
        // an update call was performed on a graph that is not locked
        // we need to get the latest version of the graph and exit edit mode
        // this situation happen when the lock goes to timeout
        window.location.reload();
    }
});

const lockLoading = computed(() =>
    store.getters.isLoading([
        GRAPH_API_TYPE.LOCK_GRAPH,
        GRAPH_API_TYPE.UNLOCK_GRAPH,
        GRAPH_API_TYPE.GET_LOCK,
    ])
);
const isLockedByAnotherUser = computed(() =>
    store.getters["graphStore/isLockedByAnotherUser"]()
);
const lockTimedOut = computed(() => store.getters["graphStore/lockTimedOut"]);
const isLocked = computed(
    () => isLockedByAnotherUser.value && !lockTimedOut.value
);
const getEditMode = computed(() => store.getters["graphStore/getEditMode"]);

watch(isLocked, (val) => {
    // in case locked state changes but the user is still in edit mode
    if (val && getEditMode.value) {
        window.location.reload();
    }
});
onMounted(() => {
    updateLatestViewedGraphs()
    window.onbeforeunload = () => {
        if (getEditMode.value) {
            // release the lock when the user refreshes the page
            store.dispatch("graphStore/unlockGraph");
        }
    };
});

const updateLatestViewedGraphs = () => {
    const storageKey = 'latestViewedGraphs'
    const latestViewedGraphs = JSON.parse(localStorage.getItem(storageKey)) || [];
    // add the current graphId on top
    if (!latestViewedGraphs.includes(graphId.value)) {
        // limit to 10 items
        if (latestViewedGraphs.length > 3) {
            latestViewedGraphs.pop();
        }
    } else {
        // if the graphId is already in the list, move it to the top
        latestViewedGraphs.splice(latestViewedGraphs.indexOf(graphId.value), 1);
    }
    latestViewedGraphs.unshift(graphId.value);
    localStorage.setItem(storageKey, JSON.stringify(latestViewedGraphs));
}

const toggleEditMode = () => store.dispatch("graphStore/toggleEditMode");

const getShowAskToStay = computed(
    () => store.getters["graphStore/getShowAskToStay"]
);
const lockGraph = () => store.dispatch("graphStore/lockGraph");

let interval = null;
watch(getShowAskToStay, (val) => {
    if (val) {
        interval = setInterval(() => {
            timeLeft.value--;
            if (timeLeft.value === 0) {
                clearInterval(interval);
            }
        }, 1000);
    } else {
        clearInterval(interval);
        timeLeft.value = 60;
    }
});
</script>

<style scoped>
.betaTag {
    margin-left: -10px;
    margin-top: -15px;
    height: 12px;
    font-size: 8px;
}

.graphTabView {
    height: calc(100% - 46px);
}

:deep(.p-tabview-panels),
:deep(.p-tabview-panel) {
    height: 100%;
}

.editModeButton {
    position: absolute;
    top: 34px;
    right: 32px;
}
</style>
